import React, { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { CreditCard, Truck, Banknote, ArrowLeft, Check, Lock, Building2, Search, MapPin, Tag, X, ChevronRight, Package } from "lucide-react";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { useCart } from "../context/CartContext";
import { useCustomerAuth } from "../context/CustomerAuthContext";
import { createOrder, createCheckoutSession, aresLookup, suggestAddress, validateCoupon, trackABConversion } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { toast } from "sonner";
import { resolveImageUrl } from "../utils/imageUrl";

const PAYMENT_METHODS = [
    { id: "stripe", label: "Platební karta", icon: CreditCard, desc: "Visa, Mastercard – bezpečná platba přes Stripe", iconColor: "text-blue-600", iconBg: "bg-blue-50" },
    { id: "cod", label: "Dobírka (+50 Kč)", icon: Truck, desc: "Platba řidiči při převzetí zásilky", iconColor: "text-orange-500", iconBg: "bg-orange-50" },
    { id: "transfer", label: "Bankovní převod", icon: Banknote, desc: "Platba převodem na účet před expedicí (QR kód)", iconColor: "text-green-600", iconBg: "bg-green-50" },
    { id: "cash", label: "Hotovost", icon: Banknote, desc: "Při osobním odběru na provozovně", iconColor: "text-gray-600", iconBg: "bg-gray-50" },
];

// Shipping → allowed payment methods mapping
const SHIPPING_PAYMENT_RULES = {
    ppl: ["stripe", "cod", "transfer"],
    zasilkovna: ["stripe", "cod", "transfer"],
    express: ["stripe", "transfer"],
    personal: ["stripe", "transfer", "cash"],  // no COD for personal pickup
};

const SHIPPING_METHODS = [
    { id: "ppl", label: "PPL Balik", desc: "Doruceni 1-2 pracovni dny", price: 199, weightSurcharge: [{above: 30, add: 100}, {above: 50, add: 250}], iconUrl: "https://www.ppl.cz/favicon.ico" },
    { id: "zasilkovna", label: "Zasilkovna", desc: "Doruceni 2-3 pracovni dny", price: 89, weightSurcharge: [{above: 10, add: 50}, {above: 20, add: 150}], iconUrl: "https://www.zasilkovna.cz/favicon.ico" },
    { id: "express", label: "PPL Express", desc: "Doruceni do 24h", price: 349, weightSurcharge: [{above: 30, add: 150}], iconUrl: "https://www.ppl.cz/favicon.ico" },
    { id: "personal", label: "Osobni odber", desc: "Kladno - zdarma", price: 0, weightSurcharge: [], iconUrl: null },
];

const STEPS = ["Fakturacni udaje", "Doprava & Platba", "Rekapitulace"];

/* PPL ParcelShop Map Widget */
function PplMapWidget({ onSelect }) {
    const containerRef = useRef(null);
    const scriptLoaded = useRef(false);

    useEffect(() => {
        if (scriptLoaded.current) return;
        // Load PPL map script and CSS
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = "https://www.ppl.cz/sources/map/main.css";
        document.head.appendChild(link);

        const script = document.createElement("script");
        script.src = "https://www.ppl.cz/sources/map/main.js";
        script.async = true;
        document.head.appendChild(script);
        scriptLoaded.current = true;

        // Listen for PPL parcelshop selection event
        const handler = (e) => {
            if (e.detail && onSelect) {
                onSelect({
                    id: e.detail.id || e.detail.code || "",
                    name: e.detail.name || "",
                    street: e.detail.street || "",
                    city: e.detail.city || "",
                    zip: e.detail.zip || e.detail.zipCode || "",
                    type: e.detail.type || "ParcelShop",
                });
            }
        };
        document.addEventListener("ppl-parcelshop-map", handler);
        return () => document.removeEventListener("ppl-parcelshop-map", handler);
    }, [onSelect]);

    return (
        <div ref={containerRef} className="rounded-lg overflow-hidden border border-gray-200" style={{ minHeight: "350px" }}>
            <div id="ppl-parcelshop-map"
                data-lat="50.08" data-lng="14.42"
                data-country="CZ"
                style={{ width: "100%", minHeight: "350px" }}
                data-testid="ppl-map-widget" />
        </div>
    );
}

export default function CheckoutPage() {
    const { items, subtotal, clearCart } = useCart();
    const { customer } = useCustomerAuth();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const [step, setStep] = useState(0);
    const [paymentMethod, setPaymentMethod] = useState("stripe");
    const [shippingMethod, setShippingMethod] = useState(searchParams.get("shipping") || "ppl");
    const [loading, setLoading] = useState(false);
    const [aresLoading, setAresLoading] = useState(false);
    const [addressSuggestions, setAddressSuggestions] = useState([]);
    const [showSuggestions, setShowSuggestions] = useState(false);
    const [termsAccepted, setTermsAccepted] = useState(false);

    // Filter shipping methods based on product restrictions
    const restrictedMethods = new Set();
    items.forEach(item => {
        (item.shipping_restrictions || []).forEach(code => restrictedMethods.add(code));
    });
    const availableShipping = SHIPPING_METHODS.filter(m => !restrictedMethods.has(m.id));
    const addressTimer = useRef(null);
    const [couponCode, setCouponCode] = useState(searchParams.get("coupon") || "");
    const [coupon, setCoupon] = useState(null);
    const [couponLoading, setCouponLoading] = useState(false);
    const [form, setForm] = useState({
        name: "", email: "", phone: "", company: "", ico: "", dic: "",
        street: "", city: "", zip_code: "", country: "CZ", notes: "",
        diff_shipping: false,
        s_street: "", s_city: "", s_zip_code: ""
    });
    const [errors, setErrors] = useState({});

    // Auto-fill form with customer data when logged in
    useEffect(() => {
        if (customer && customer.email) {
            setForm(prev => ({
                ...prev,
                name: customer.name || prev.name,
                email: customer.email || prev.email,
                phone: customer.phone || prev.phone,
                company: customer.company || prev.company,
                ico: customer.ico || prev.ico,
                dic: customer.dic || prev.dic,
            }));
            // Try to load default address
            const token = localStorage.getItem("sg_customer_token");
            if (token) {
                import("../lib/api").then(({ default: api }) => {
                    api.get("/customer/addresses").then(r => {
                        const addrs = r.data || [];
                        const def = addrs.find(a => a.is_default) || addrs[0];
                        if (def) {
                            setForm(prev => ({
                                ...prev,
                                street: def.street || prev.street,
                                city: def.city || prev.city,
                                zip_code: def.zip || def.zip_code || prev.zip_code,
                            }));
                        }
                    }).catch(() => {});
                });
            }
        }
    }, [customer]);

    // Auto-validate coupon from URL (passed from CartPage)
    useEffect(() => {
        const code = searchParams.get("coupon");
        if (code && !coupon && !couponLoading) {
            setCouponCode(code);
            (async () => {
                setCouponLoading(true);
                try {
                    const res = await validateCoupon(code);
                    setCoupon(res.data);
                } catch {
                    setCoupon(null);
                }
                setCouponLoading(false);
            })();
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Calculate total cart weight
    const totalWeight = items.reduce((sum, item) => sum + (parseFloat(item.weight_kg) || 0) * (item.quantity || 1), 0);

    // Calculate shipping price (base + weight surcharge)
    const getShippingPrice = (method) => {
        if (!method) return 199;
        let price = method.price;
        if (method.weightSurcharge && totalWeight > 0) {
            for (const s of method.weightSurcharge) {
                if (totalWeight > s.above) price = method.price + s.add;
            }
        }
        return price;
    };

    const currentShippingMethod = SHIPPING_METHODS.find(s => s.id === shippingMethod) || availableShipping[0];
    const shippingCost = getShippingPrice(currentShippingMethod);

    // Filter payment methods by shipping selection
    const allowedPaymentIds = SHIPPING_PAYMENT_RULES[shippingMethod] || ["stripe", "cod", "transfer", "cash"];
    const availablePayments = PAYMENT_METHODS.filter(p => allowedPaymentIds.includes(p.id));

    // Auto-switch payment if current is not allowed
    useEffect(() => {
        if (!allowedPaymentIds.includes(paymentMethod)) {
            setPaymentMethod(allowedPaymentIds[0] || "stripe");
        }
    }, [shippingMethod]);

    const codFee = paymentMethod === "cod" ? 50 : 0;
    const discount = coupon ? Math.round(subtotal * coupon.value / 100) : 0;
    const total = subtotal + shippingCost + codFee - discount;
    const vatAmount = Math.round(total * 0.21 / 1.21);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
        if (errors[name]) setErrors(prev => ({ ...prev, [name]: "" }));

        // Address autocomplete
        if (name === "street" && value.length >= 3) {
            clearTimeout(addressTimer.current);
            addressTimer.current = setTimeout(async () => {
                try {
                    const res = await suggestAddress(value);
                    setAddressSuggestions(res.data || []);
                    setShowSuggestions(true);
                } catch {}
            }, 400);
        }
    };

    const handleSelectAddress = (addr) => {
        setForm(prev => ({
            ...prev,
            street: addr.street,
            city: addr.city,
            zip_code: addr.zip_code,
        }));
        setShowSuggestions(false);
        setAddressSuggestions([]);
    };

    const handleAresLookup = async () => {
        if (!form.ico) { toast.error("Zadejte IČO"); return; }
        setAresLoading(true);
        try {
            const res = await aresLookup(form.ico);
            const d = res.data;
            setForm(prev => ({
                ...prev,
                company: d.name || prev.company,
                dic: d.dic || prev.dic,
                street: d.street || prev.street,
                city: d.city || prev.city,
                zip_code: d.zip_code || prev.zip_code,
            }));
            toast.success(`Firma načtena: ${d.name}`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Firma nenalezena");
        } finally {
            setAresLoading(false);
        }
    };

    const handleCoupon = async () => {
        if (!couponCode.trim()) return;
        setCouponLoading(true);
        try {
            const res = await validateCoupon(couponCode.trim());
            setCoupon(res.data);
            toast.success(`Kupón "${res.data.name}" uplatněn`);
        } catch (err) {
            toast.error(err.response?.data?.detail || "Neplatný kupón");
            setCoupon(null);
        } finally {
            setCouponLoading(false);
        }
    };

    const validate = () => {
        const errs = {};
        if (!form.name.trim()) errs.name = "Povinné pole";
        if (!form.email.trim() || !/\S+@\S+\.\S+/.test(form.email)) errs.email = "Zadejte platný email";
        if (!form.phone.trim()) errs.phone = "Povinné pole";
        if (!form.street.trim()) errs.street = "Povinné pole";
        if (!form.city.trim()) errs.city = "Povinné pole";
        if (!form.zip_code.trim()) errs.zip_code = "Povinné pole";
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleNextStep = () => {
        if (step === 0 && !validate()) {
            toast.error("Vyplňte povinná pole");
            return;
        }
        setStep(s => Math.min(s + 1, 2));
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const handleSubmit = async () => {
        if (items.length === 0) { toast.error("Košík je prázdný"); return; }
        setLoading(true);
        try {
            const shippingAddr = form.diff_shipping ? { street: form.s_street, city: form.s_city, zip_code: form.s_zip_code, country: "CZ" } : null;
            const orderData = {
                items: items.map(i => ({
                    product_id: i.product_id, sku: i.sku, name: i.name,
                    quantity: i.quantity, unit_price: i.price, total_price: i.price * i.quantity, image: i.image
                })),
                customer: { name: form.name, email: form.email, phone: form.phone, company: form.company, ico: form.ico, dic: form.dic },
                billing_address: { street: form.street, city: form.city, zip_code: form.zip_code, country: form.country },
                shipping_address: shippingAddr,
                payment_method: paymentMethod,
                shipping_method: shippingMethod,
                shipping_cost: shippingCost,
                cod_fee: codFee,
                notes: form.notes,
                coupon_code: coupon ? couponCode : null,
                coupon_discount: discount,
            };
            const orderRes = await createOrder(orderData);
            const order = orderRes.data;

            // Fire A/B conversion tracking for products with stored variants
            try {
                const abKeys = Object.keys(sessionStorage).filter(k => k.startsWith("ab_variant_"));
                const fired = new Set();
                abKeys.forEach(key => {
                    const abData = JSON.parse(sessionStorage.getItem(key));
                    if (abData?.variant && !fired.has(abData.entity_slug)) {
                        fired.add(abData.entity_slug);
                        trackABConversion({
                            entity_type: abData.entity_type || "product",
                            entity_slug: abData.entity_slug,
                            variant: abData.variant,
                            event_type: "order_completed",
                        }).catch(() => {});
                    }
                    sessionStorage.removeItem(key);
                });
            } catch {}

            if (paymentMethod === "stripe") {
                const sessionRes = await createCheckoutSession({ order_id: order.id, origin_url: window.location.origin });
                window.location.href = sessionRes.data.url;
            } else {
                clearCart();
                navigate(`/dekujeme?order_id=${order.id}&order_number=${order.order_number}&payment_method=${paymentMethod}`);
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || "Chyba při vytváření objednávky");
        } finally {
            setLoading(false);
        }
    };

    if (items.length === 0) {
        return (
            <div className="min-h-screen bg-gray-50">
                <Navbar />
                <div className="max-w-xl mx-auto px-4 py-24 text-center">
                    <h2 className="text-2xl font-bold text-gray-800 mb-4">Košík je prázdný</h2>
                    <Button asChild style={{ backgroundColor: "var(--sg-primary)" }} className="text-white">
                        <Link to="/kategorie">Pokračovat v nákupu</Link>
                    </Button>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50">
            <Navbar />
            <div className="max-w-6xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <Link to="/kosik" className="p-2 hover:bg-gray-100 rounded-lg transition-colors">
                        <ArrowLeft size={18} />
                    </Link>
                    <h1 className="text-2xl font-bold text-gray-900">Pokladna</h1>
                    <div className="flex items-center gap-1.5 ml-auto text-sm text-gray-500">
                        <Lock size={13} /> Zabezpečená platba
                    </div>
                </div>

                {/* Step indicator */}
                <div className="flex items-center mb-8 bg-white rounded-xl border border-gray-100 p-4">
                    {STEPS.map((s, i) => (
                        <React.Fragment key={i}>
                            <button onClick={() => i < step && setStep(i)}
                                className={`flex items-center gap-2 text-sm font-semibold transition-colors ${i === step ? "text-green-700" : i < step ? "text-green-600 cursor-pointer" : "text-gray-300"}`}>
                                <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${i === step ? "text-white" : i < step ? "bg-green-500 text-white" : "bg-gray-100 text-gray-400"}`}
                                    style={i === step ? { backgroundColor: "var(--sg-primary)" } : {}}>
                                    {i < step ? <Check size={13} /> : i + 1}
                                </div>
                                <span className="hidden sm:block">{s}</span>
                            </button>
                            {i < STEPS.length - 1 && (
                                <div className={`flex-1 h-0.5 mx-3 ${i < step ? "bg-green-400" : "bg-gray-100"}`} />
                            )}
                        </React.Fragment>
                    ))}
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                    {/* Main form area */}
                    <div className="lg:col-span-2">
                        {/* Step 0: Billing info */}
                        {step === 0 && (
                            <div className="space-y-5">
                                <div className="bg-white rounded-xl border border-gray-100 p-6">
                                    <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
                                        <Building2 size={18} style={{ color: "var(--sg-primary)" }} /> Fakturační údaje
                                    </h2>
                                    <div className="grid grid-cols-2 gap-4">
                                        <Field label="Jméno a příjmení" name="name" value={form.name} onChange={handleChange} required error={errors.name} />
                                        <Field label="Email" name="email" value={form.email} onChange={handleChange} required type="email" error={errors.email} half />
                                        <Field label="Telefon" name="phone" value={form.phone} onChange={handleChange} required placeholder="+420" error={errors.phone} half />
                                        <div className="col-span-2 border-t border-gray-100 pt-4">
                                            <p className="text-xs text-gray-500 mb-3 font-semibold uppercase tracking-wide">Firma (volitelné)</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                <div className="col-span-2 flex gap-2">
                                                    <div className="flex-1">
                                                        <Label className="text-sm font-medium text-gray-700 mb-1.5 block">IČO</Label>
                                                        <div className="flex gap-2">
                                                            <Input name="ico" value={form.ico} onChange={handleChange}
                                                                placeholder="12345678" className="h-10 flex-1"
                                                                data-testid="checkout-ico" />
                                                            <Button type="button" onClick={handleAresLookup}
                                                                disabled={aresLoading || !form.ico}
                                                                variant="outline" className="h-10 px-3 text-xs flex-shrink-0 border-green-300 text-green-700 hover:bg-green-50"
                                                                data-testid="ares-lookup-btn">
                                                                {aresLoading ? "..." : <><Search size={13} className="mr-1" />ARES</>}
                                                            </Button>
                                                        </div>
                                                    </div>
                                                    <Field label="DIČ" name="dic" value={form.dic} onChange={handleChange} half />
                                                </div>
                                                <Field label="Název firmy" name="company" value={form.company} onChange={handleChange} />
                                            </div>
                                        </div>
                                        <div className="col-span-2 border-t border-gray-100 pt-4">
                                            <p className="text-xs text-gray-500 mb-3 font-semibold uppercase tracking-wide">Adresa</p>
                                            <div className="grid grid-cols-2 gap-4">
                                                {/* Street with autocomplete */}
                                                <div className="col-span-2 relative">
                                                    <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                                                        Ulice a číslo popisné <span className="text-red-500">*</span>
                                                    </Label>
                                                    <div className="relative">
                                                        <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                                                        <Input name="street" value={form.street} onChange={handleChange}
                                                            placeholder="Náměstí Míru 15" className="h-10 pl-8"
                                                            data-testid="checkout-street"
                                                            onFocus={() => addressSuggestions.length > 0 && setShowSuggestions(true)}
                                                            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)} />
                                                        {errors.street && <p className="text-xs text-red-500 mt-1">{errors.street}</p>}
                                                    </div>
                                                    {showSuggestions && addressSuggestions.length > 0 && (
                                                        <div className="absolute top-full left-0 right-0 bg-white border border-gray-200 rounded-xl shadow-lg z-50 mt-1 overflow-hidden">
                                                            {addressSuggestions.map((addr, i) => (
                                                                <button key={i} type="button"
                                                                    className="w-full flex items-start gap-2 px-4 py-3 text-sm hover:bg-gray-50 transition-colors border-b border-gray-50 last:border-0 text-left"
                                                                    onMouseDown={() => handleSelectAddress(addr)}>
                                                                    <MapPin size={13} className="text-gray-400 mt-0.5 flex-shrink-0" />
                                                                    <div>
                                                                        <p className="font-medium text-gray-800">{addr.street}</p>
                                                                        <p className="text-xs text-gray-400">{addr.zip_code} {addr.city}</p>
                                                                    </div>
                                                                </button>
                                                            ))}
                                                        </div>
                                                    )}
                                                </div>
                                                <Field label="Město" name="city" value={form.city} onChange={handleChange} required error={errors.city} half />
                                                <Field label="PSČ" name="zip_code" value={form.zip_code} onChange={handleChange} required error={errors.zip_code} half />
                                            </div>
                                        </div>
                                        {/* Different shipping address */}
                                        <div className="col-span-2">
                                            <label className="flex items-center gap-2 cursor-pointer text-sm text-gray-600">
                                                <input type="checkbox" name="diff_shipping" checked={form.diff_shipping} onChange={handleChange} className="rounded" />
                                                Doručit na jinou adresu
                                            </label>
                                            {form.diff_shipping && (
                                                <div className="grid grid-cols-2 gap-4 mt-3 pt-3 border-t border-gray-100">
                                                    <Field label="Ulice (doručovací)" name="s_street" value={form.s_street} onChange={handleChange} />
                                                    <Field label="Město" name="s_city" value={form.s_city} onChange={handleChange} half />
                                                    <Field label="PSČ" name="s_zip_code" value={form.s_zip_code} onChange={handleChange} half />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                                <Button onClick={handleNextStep} className="w-full h-12 text-white font-semibold text-base"
                                    style={{ backgroundColor: "var(--sg-primary)" }}>
                                    Pokračovat k dopravě <ChevronRight size={16} />
                                </Button>
                            </div>
                        )}

                        {/* Step 1: Shipping & Payment */}
                        {step === 1 && (
                            <div className="space-y-5">
                                <div className="bg-white rounded-xl border border-gray-100 p-6">
                                    <h2 className="font-semibold text-gray-900 mb-4">Způsob dopravy</h2>
                                    {restrictedMethods.size > 0 && (
                                        <p className="text-xs text-amber-600 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 mb-3" data-testid="shipping-restriction-notice">
                                            Některé způsoby dopravy nejsou dostupné pro produkty ve vašem košíku.
                                        </p>
                                    )}
                                    <div className="space-y-2">
                                        {availableShipping.map(m => {
                                            const methodPrice = getShippingPrice(m);
                                            return (
                                            <label key={m.id} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${shippingMethod === m.id ? "border-green-500 bg-green-50" : "border-gray-100 hover:border-gray-200"}`}>
                                                <input type="radio" name="shipping" value={m.id}
                                                    checked={shippingMethod === m.id}
                                                    onChange={() => setShippingMethod(m.id)} className="hidden"
                                                    data-testid={`shipping-${m.id}`} />
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${shippingMethod === m.id ? "border-green-500" : "border-gray-300"}`}>
                                                    {shippingMethod === m.id && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
                                                </div>
                                                <div className="w-8 h-8 rounded-lg bg-gray-100 flex items-center justify-center flex-shrink-0 overflow-hidden">
                                                    {m.iconUrl ? <img src={m.iconUrl} alt="" className="w-5 h-5 object-contain" onError={e => { e.target.style.display = 'none'; }} />
                                                        : <Package size={16} className="text-gray-400" />}
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-800 text-sm">{m.label}</p>
                                                    <p className="text-xs text-gray-500">{m.desc}</p>
                                                    {totalWeight > 0 && methodPrice > m.price && (
                                                        <p className="text-[10px] text-amber-600 mt-0.5">+{methodPrice - m.price} Kc priplatek za hmotnost ({totalWeight.toFixed(1)} kg)</p>
                                                    )}
                                                </div>
                                                <span className="font-semibold text-gray-900 text-sm">
                                                    {methodPrice === 0 ? <span className="text-green-600">Zdarma</span> : `${methodPrice} Kc`}
                                                </span>
                                            </label>
                                        )})}
                                    </div>

                                    {/* PPL Pickup Map */}
                                    {(shippingMethod === "ppl" || shippingMethod === "express") && (
                                        <div className="mt-4 p-4 bg-blue-50/50 border border-blue-100 rounded-xl" data-testid="ppl-map-section">
                                            <div className="flex items-center justify-between mb-3">
                                                <h3 className="text-sm font-semibold text-gray-800 flex items-center gap-2">
                                                    <MapPin size={14} className="text-blue-500" />
                                                    PPL ParcelShop / ParcelBox
                                                </h3>
                                                <span className="text-[10px] text-gray-400">Vyberte vydejni misto z mapy</span>
                                            </div>
                                            <PplMapWidget onSelect={(point) => {
                                                setForm(prev => ({ ...prev, ppl_point_name: point.name, ppl_point_id: point.id, ppl_point_address: `${point.street}, ${point.city} ${point.zip}` }));
                                                toast.success(`Vybrano: ${point.name}`);
                                            }} />
                                            {form.ppl_point_name && (
                                                <div className="mt-2 flex items-center gap-2 bg-white px-3 py-2 rounded-lg border border-green-200 text-sm">
                                                    <Check size={14} className="text-green-500 shrink-0" />
                                                    <div>
                                                        <span className="font-medium text-gray-800">{form.ppl_point_name}</span>
                                                        <span className="text-xs text-gray-500 ml-2">{form.ppl_point_address}</span>
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    )}
                                </div>

                                <div className="bg-white rounded-xl border border-gray-100 p-6">
                                    <h2 className="font-semibold text-gray-900 mb-4">Způsob platby</h2>
                                    <div className="space-y-2">
                                        {availablePayments.map(m => (
                                            <label key={m.id} className={`flex items-center gap-4 p-4 rounded-xl border-2 cursor-pointer transition-colors ${paymentMethod === m.id ? "border-green-500 bg-green-50" : "border-gray-100 hover:border-gray-200"}`}
                                                data-testid={`payment-option-${m.id}`}>
                                                <input type="radio" name="payment" value={m.id}
                                                    checked={paymentMethod === m.id}
                                                    onChange={() => setPaymentMethod(m.id)} className="hidden"
                                                    data-testid={`payment-${m.id}`} />
                                                <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${paymentMethod === m.id ? "border-green-500" : "border-gray-300"}`}>
                                                    {paymentMethod === m.id && <div className="w-2.5 h-2.5 rounded-full bg-green-500" />}
                                                </div>
                                                <div className={`w-10 h-10 rounded-xl ${m.iconBg || "bg-gray-100"} flex items-center justify-center flex-shrink-0 ${m.iconColor || "text-gray-500"}`}>
                                                    <m.icon size={20} />
                                                </div>
                                                <div className="flex-1">
                                                    <p className="font-medium text-gray-800 text-sm">{m.label}</p>
                                                    <p className="text-xs text-gray-500">{m.desc}</p>
                                                </div>
                                                {paymentMethod === m.id && <Check size={16} className="text-green-600 flex-shrink-0" />}
                                            </label>
                                        ))}
                                    </div>
                                    {availablePayments.length < PAYMENT_METHODS.length && (
                                        <p className="text-xs text-gray-400 mt-2 italic">Některé platební metody nejsou dostupné pro zvolený způsob dopravy.</p>
                                    )}
                                </div>

                                {/* Coupon */}
                                <div className="bg-white rounded-xl border border-gray-100 p-5">
                                    <h3 className="font-semibold text-gray-800 mb-3 flex items-center gap-2 text-sm">
                                        <Tag size={14} style={{ color: "var(--sg-primary)" }} /> Slevový kupón
                                    </h3>
                                    {coupon ? (
                                        <div className="flex items-center gap-3 bg-green-50 border border-green-200 rounded-xl p-3">
                                            <Tag size={16} className="text-green-600" />
                                            <div className="flex-1">
                                                <p className="font-semibold text-green-800 text-sm">{coupon.name} – sleva {coupon.value}%</p>
                                                <p className="text-xs text-green-600">Ušetříte {discount.toLocaleString("cs-CZ")} Kč</p>
                                            </div>
                                            <button onClick={() => { setCoupon(null); setCouponCode(""); }} className="text-green-600 hover:text-green-800">
                                                <X size={14} />
                                            </button>
                                        </div>
                                    ) : (
                                        <div className="flex gap-2">
                                            <Input value={couponCode} onChange={e => setCouponCode(e.target.value.toUpperCase())}
                                                placeholder="Kód kupónu..." className="flex-1 h-10 text-sm font-mono tracking-widest"
                                                data-testid="checkout-coupon" onKeyDown={e => e.key === "Enter" && handleCoupon()} />
                                            <Button onClick={handleCoupon} disabled={couponLoading || !couponCode.trim()}
                                                style={{ backgroundColor: "var(--sg-primary)" }} className="h-10 px-4 text-white">
                                                {couponLoading ? "..." : "Uplatnit"}
                                            </Button>
                                        </div>
                                    )}
                                </div>

                                {/* Notes */}
                                <div className="bg-white rounded-xl border border-gray-100 p-5">
                                    <Label className="text-sm font-medium text-gray-700 mb-1.5 block">Poznámka k objednávce</Label>
                                    <textarea name="notes" value={form.notes} onChange={handleChange}
                                        placeholder="Speciální požadavky, termín doručení..."
                                        className="w-full h-20 px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-green-200 resize-none"
                                        data-testid="checkout-notes" />
                                </div>

                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={() => setStep(0)} className="flex-1 h-12">
                                        <ArrowLeft size={16} className="mr-2" /> Zpět
                                    </Button>
                                    <Button onClick={handleNextStep} className="flex-1 h-12 text-white font-semibold"
                                        style={{ backgroundColor: "var(--sg-primary)" }}>
                                        Rekapitulace <ChevronRight size={16} />
                                    </Button>
                                </div>
                            </div>
                        )}

                        {/* Step 2: Summary & Confirm */}
                        {step === 2 && (
                            <div className="space-y-5">
                                <div className="bg-white rounded-xl border border-gray-100 p-6">
                                    <h2 className="font-semibold text-gray-900 mb-4">Rekapitulace objednávky</h2>
                                    <div className="grid grid-cols-2 gap-4 text-sm mb-4">
                                        <div>
                                            <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Zákazník</p>
                                            <p className="font-medium text-gray-800">{form.name}</p>
                                            <p className="text-gray-600">{form.email}</p>
                                            <p className="text-gray-600">{form.phone}</p>
                                            {form.company && <p className="text-gray-600 mt-1">{form.company}</p>}
                                            {form.ico && <p className="text-gray-500 text-xs">IČO: {form.ico}</p>}
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Adresa</p>
                                            <p className="text-gray-800">{form.street}</p>
                                            <p className="text-gray-800">{form.zip_code} {form.city}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Doprava</p>
                                            <p className="text-gray-800">{SHIPPING_METHODS.find(s => s.id === shippingMethod)?.label}</p>
                                        </div>
                                        <div>
                                            <p className="text-xs text-gray-400 font-semibold uppercase mb-1">Platba</p>
                                            <p className="text-gray-800">{PAYMENT_METHODS.find(p => p.id === paymentMethod)?.label}</p>
                                        </div>
                                    </div>
                                    <div className="border-t border-gray-100 pt-4 space-y-2">
                                        {items.map(item => (
                                            <div key={item.product_id} className="flex gap-3 items-center">
                                                <div className="w-10 h-10 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0 border border-gray-100">
                                                    <img src={resolveImageUrl(item.image) || ""} alt="" className="w-full h-full object-contain" />
                                                </div>
                                                <p className="flex-1 text-sm text-gray-700 line-clamp-1">{item.name}</p>
                                                <p className="text-sm text-gray-500">{item.quantity} ks</p>
                                                <div className="text-right">
                                                    <p className="text-sm font-semibold text-gray-800">{(item.price * item.quantity).toLocaleString("cs-CZ")} Kč</p>
                                                    {item.original_price && item.original_price > item.price && (
                                                        <p className="text-[10px] text-gray-400 line-through">{(item.original_price * item.quantity).toLocaleString("cs-CZ")} Kč</p>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                    {/* Summary totals in rekapitulace */}
                                    <div className="border-t border-gray-100 pt-4 mt-4 space-y-1.5 text-sm">
                                        <div className="flex justify-between text-gray-600">
                                            <span>Mezisoučet</span>
                                            <span>{subtotal.toLocaleString("cs-CZ")} Kč</span>
                                        </div>
                                        <div className="flex justify-between text-gray-600">
                                            <span>Doprava ({SHIPPING_METHODS.find(s => s.id === shippingMethod)?.label})</span>
                                            <span>{shippingCost === 0 ? "Zdarma" : `${shippingCost} Kč`}</span>
                                        </div>
                                        {codFee > 0 && (
                                            <div className="flex justify-between text-gray-600">
                                                <span>Příplatek dobírka</span>
                                                <span>{codFee} Kč</span>
                                            </div>
                                        )}
                                        {coupon && discount > 0 && (
                                            <div className="flex justify-between text-green-600 font-semibold" data-testid="recap-coupon-discount">
                                                <span>Sleva ({coupon.name} – {coupon.value}%)</span>
                                                <span>−{discount.toLocaleString("cs-CZ")} Kč</span>
                                            </div>
                                        )}
                                        <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-100 pt-2 mt-2">
                                            <span>Celkem s DPH</span>
                                            <span>{total.toLocaleString("cs-CZ")} Kč</span>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-white rounded-xl border border-gray-100 p-5">
                                    <label className="flex items-start gap-3 cursor-pointer" data-testid="checkout-terms-checkbox">
                                        <input type="checkbox" checked={termsAccepted} onChange={e => setTermsAccepted(e.target.checked)}
                                            className="mt-1 rounded border-gray-300 text-green-600 focus:ring-green-500" />
                                        <span className="text-sm text-gray-600 leading-relaxed">
                                            Souhlasím s{" "}
                                            <Link to="/stranka/obchodni-podminky" target="_blank" className="text-green-600 underline hover:text-green-700">obchodními podmínkami</Link>,{" "}
                                            <Link to="/stranka/reklamacni-rad" target="_blank" className="text-green-600 underline hover:text-green-700">reklamačním řádem</Link>,{" "}
                                            <Link to="/stranka/odstoupeni-od-smlouvy" target="_blank" className="text-green-600 underline hover:text-green-700">podmínkami odstoupení od smlouvy</Link>{" "}
                                            a beru na vědomí{" "}
                                            <Link to="/stranka/ochrana-osobnich-udaju" target="_blank" className="text-green-600 underline hover:text-green-700">zásady ochrany osobních údajů</Link>.
                                        </span>
                                    </label>
                                </div>

                                <div className="flex gap-3">
                                    <Button variant="outline" onClick={() => setStep(1)} className="flex-1 h-12">
                                        <ArrowLeft size={16} className="mr-2" /> Zpět
                                    </Button>
                                    <Button onClick={handleSubmit} disabled={loading || !termsAccepted}
                                        className="flex-2 h-12 text-white font-semibold text-base px-8"
                                        style={{ backgroundColor: !termsAccepted ? "#9ca3af" : "var(--sg-primary)" }}
                                        data-testid="checkout-submit-btn">
                                        {loading ? "Zpracovávám..." : paymentMethod === "stripe" ? "Zaplatit kartou" : "Potvrdit objednávku"}
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>

                    {/* Sticky Summary */}
                    <div>
                        <div className="bg-white rounded-xl border border-gray-100 p-6 sticky top-20">
                            <h2 className="font-semibold text-gray-900 mb-4">Váš košík</h2>
                            <div className="space-y-3 mb-5 max-h-64 overflow-y-auto pr-1">
                                {items.map(item => (
                                    <div key={item.product_id} className="flex gap-3">
                                        <div className="w-12 h-12 bg-gray-50 rounded-lg overflow-hidden flex-shrink-0">
                                            <img src={resolveImageUrl(item.image) || ""} alt="" className="w-full h-full object-contain" />
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-gray-800 line-clamp-1">{item.name}</p>
                                            <p className="text-xs text-gray-500">{item.quantity} ks × {item.price.toLocaleString("cs-CZ")} Kč</p>
                                        </div>
                                        <span className="text-sm font-semibold text-gray-800 flex-shrink-0">
                                            {(item.price * item.quantity).toLocaleString("cs-CZ")} Kč
                                        </span>
                                    </div>
                                ))}
                            </div>
                            <div className="space-y-2 text-sm border-t border-gray-100 pt-4">
                                <div className="flex justify-between text-gray-600">
                                    <span>Mezisoučet</span>
                                    <span>{subtotal.toLocaleString("cs-CZ")} Kč</span>
                                </div>
                                <div className="flex justify-between text-gray-600">
                                    <span>Doprava</span>
                                    <span>{shippingCost === 0 ? <span className="text-green-600">Zdarma</span> : `${shippingCost} Kč`}</span>
                                </div>
                                {codFee > 0 && (
                                    <div className="flex justify-between text-gray-600">
                                        <span>Příplatek dobírka</span>
                                        <span>{codFee} Kč</span>
                                    </div>
                                )}
                                {coupon && (
                                    <div className="flex justify-between text-green-600 font-semibold">
                                        <span>Sleva ({coupon.name})</span>
                                        <span>−{discount.toLocaleString("cs-CZ")} Kč</span>
                                    </div>
                                )}
                                <div className="flex justify-between text-xs text-gray-400">
                                    <span>z toho DPH 21%</span>
                                    <span>{vatAmount.toLocaleString("cs-CZ")} Kč</span>
                                </div>
                                <div className="flex justify-between font-bold text-gray-900 text-base border-t border-gray-100 pt-2 mt-2">
                                    <span>Celkem s DPH</span>
                                    <span data-testid="checkout-total">{total.toLocaleString("cs-CZ")} Kč</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
}

function Field({ label, name, value, onChange, required, type = "text", placeholder = "", half = false, error }) {
    return (
        <div className={half ? "col-span-1" : "col-span-2"}>
            <Label className="text-sm font-medium text-gray-700 mb-1.5 block">
                {label}{required && <span className="text-red-500 ml-1">*</span>}
            </Label>
            <Input type={type} name={name} value={value} onChange={onChange} required={required}
                placeholder={placeholder} className={`h-10 ${error ? "border-red-300 focus-visible:ring-red-300" : ""}`}
                data-testid={`checkout-${name}`} />
            {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
        </div>
    );
}
